import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { maintenanceConfig } from '../config/maintenance';

const MaintenanceContext = createContext(null);

const normalizeSettings = (raw) => {
  const fallback = maintenanceConfig || {};
  const enabled = Boolean(raw?.enabled ?? fallback.enabled);
  const message = String(raw?.message ?? fallback.message ?? '');
  const estimatedEnd = String(raw?.estimatedEnd ?? raw?.estimated_end ?? fallback.estimatedEnd ?? '');
  const allowedRolesRaw = raw?.allowedRoles ?? raw?.allowed_roles ?? fallback.allowedRoles ?? ['admin'];
  const allowedRoles = Array.isArray(allowedRolesRaw)
    ? allowedRolesRaw.map((v) => String(v || '').toLowerCase()).filter(Boolean)
    : ['admin'];
  return {
    enabled,
    message,
    estimatedEnd,
    allowedRoles,
  };
};

export function MaintenanceProvider({ children }) {
  const [settings, setSettings] = useState(() => normalizeSettings(maintenanceConfig));
  const [loading, setLoading] = useState(true);
  const lastLoadErrorRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/maintenance');
      const normalized = normalizeSettings(res.data || {});
      setSettings(normalized);
      lastLoadErrorRef.current = null;
      return normalized;
    } catch (error) {
      lastLoadErrorRef.current = error;
      const fallback = normalizeSettings(maintenanceConfig);
      setSettings(fallback);
      return fallback;
    } finally {
      setLoading(false);
    }
  }, []);

  const update = useCallback(async (nextSettings) => {
    const payload = {
      enabled: Boolean(nextSettings?.enabled),
      message: String(nextSettings?.message || ''),
      estimatedEnd: String(nextSettings?.estimatedEnd || ''),
      allowedRoles: Array.isArray(nextSettings?.allowedRoles)
        ? nextSettings.allowedRoles
        : ['admin'],
    };
    const res = await api.put('/maintenance', payload);
    const normalized = normalizeSettings(res.data || payload);
    setSettings(normalized);
    return normalized;
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const id = setInterval(() => {
      refresh();
    }, 60_000);
    return () => clearInterval(id);
  }, [refresh]);

  const value = useMemo(
    () => ({
      ...settings,
      loading,
      refresh,
      update,
    }),
    [settings, loading, refresh, update]
  );

  return <MaintenanceContext.Provider value={value}>{children}</MaintenanceContext.Provider>;
}

export function useMaintenance() {
  const ctx = useContext(MaintenanceContext);
  if (!ctx) {
    throw new Error('useMaintenance must be used within MaintenanceProvider');
  }
  return ctx;
}

